import React from "react";
import Empty from "../../../components/v3/Empty";

/**
 * ApprovalContextCards - everything known about the investor behind one SLA.
 *
 * Lifted out of DRApprovalDetail because the compliance decision-review screen
 * shows the same context: a CCO reversing a DR's call needs the same profile,
 * classifications, suitability history and KYC trail the DR saw. Both screens
 * read it from the same `GET admin/dr/approvals/:slaId` payload, so this takes
 * that payload whole rather than a dozen props.
 */

// Human-readable labels for the structured exemption criteria captured on
// investor_classification (2026-07 compliance package). Keys mirror the
// API enum values exactly.
const AI_PARAGRAPH_LABELS = {
  income_200k:  "Income over $200k",
  spousal_300k: "Spousal income over $300k",
  fin_assets_1m: "Financial assets over $1M",
  net_assets_5m: "Net assets over $5M",
};

const ENTITY_AI_CATEGORY_LABELS = {
  fin_institution:  "Canadian financial institution",
  registrant:       "Registered adviser or dealer",
  pension_fund:     "Regulated pension fund",
  government:       "Government or agency",
  net_assets_5m:    "Entity net assets over $5M",
  investment_fund:  "Investment fund (AI-only)",
  all_owners_ai:    "All owners are accredited",
  permitted_client: "Permitted client (NI 31-103)",
  analogous_foreign_institution: "Analogous foreign institution",
};

const ELIGIBLE_PARAGRAPH_LABELS = {
  income_75k:      "Income over $75k",
  spousal_125k:    "Spousal income over $125k",
  net_assets_400k: "Net assets over $400k",
};

const FFBA_RELATIONSHIP_LABELS = {
  FAMILY:                   "Family member",
  CLOSE_PERSONAL_FRIEND:    "Close personal friend",
  CLOSE_BUSINESS_ASSOCIATE: "Close business associate",
};

// JSON columns arrive as arrays through the API, but tolerate stringified
// round-trips from older rows.
function asArray(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  }
  return [];
}

// Declared exemption criteria for one classification row, as display lines.
function classificationCriteria(c) {
  const lines = [];
  const ai = asArray(c.ai_paragraphs).map((k) => AI_PARAGRAPH_LABELS[k] || k);
  if (ai.length) lines.push(ai.join("; "));
  const entity = asArray(c.entity_ai_categories).map((k) => ENTITY_AI_CATEGORY_LABELS[k] || k);
  if (entity.length) lines.push(entity.join("; "));
  if (c.eligible_paragraph) {
    lines.push(ELIGIBLE_PARAGRAPH_LABELS[c.eligible_paragraph] || c.eligible_paragraph);
  }
  if (c.ffba_relationship_type) {
    let ffba = `FFBA: ${FFBA_RELATIONSHIP_LABELS[c.ffba_relationship_type] || c.ffba_relationship_type}`;
    if (c.ffba_connected_name) {
      ffba += `, connected to ${c.ffba_connected_name}`;
      if (c.ffba_connected_position) ffba += ` (${c.ffba_connected_position})`;
    }
    if (c.ffba_years_known !== null && c.ffba_years_known !== undefined) {
      ffba += `, known ${c.ffba_years_known} ${Number(c.ffba_years_known) === 1 ? "year" : "years"}`;
    }
    lines.push(ffba);
  }
  return lines;
}

/**
 * personName - name whoever this is, whichever shape they arrived in.
 *
 * The API hands the same person over two ways: the queue and the decisions
 * list flatten them to `{id, name, email}`, while the approval DETAIL returns
 * the plain user row with `firstname` / `lastname` and no `name` at all.
 * Reading only `name` made every detail screen show an email address where a
 * name was sitting one field away.
 */
export function personName(p, fallbackId) {
  if (p) {
    const full = `${p.firstname || ""} ${p.lastname || ""}`.trim();
    return p.name || full || p.email || `Reviewer #${p.id}`;
  }
  return fallbackId ? `Reviewer #${fallbackId}` : "-";
}

export function investorName(inv) {
  if (!inv) return "Investor";
  const full = `${inv.firstname || ""} ${inv.lastname || ""}`.trim();
  return full || inv.email || `User #${inv.id}`;
}

export const KeyVal = ({ label, value }) => (
  <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--bd)" }}>
    <span className="field__label" style={{ alignSelf: "center" }}>{label}</span>
    <span style={{ fontSize: "var(--t-md)", color: "var(--tx)" }}>{value ?? "-"}</span>
  </div>
);

const ListItem = ({ children, meta }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--bd)" }}>
    <div>{children}</div>
    <div className="mono" style={{ fontSize: "var(--t-xs)", color: "var(--tx-3)" }}>{meta}</div>
  </div>
);

function ApprovalContextCards({ payload }) {
  const investor = payload?.investor;
  const investment = payload?.investment;
  const ctx = payload?.context || {};

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: "var(--gap-y)",
      }}
    >
      <section className="card">
        <div className="card__head"><div className="card__title">Investor profile</div></div>
        <div className="card__body">
          <KeyVal label="Email" value={investor?.email} />
          <KeyVal label="Phone" value={investor?.phone} />
          <KeyVal label="KYC status" value={investor?.KYC_status || "-"} />
          <KeyVal label="Accredited" value={investor?.isAccreditedInvestor ? "Yes" : "No"} />
          <KeyVal label="Country" value={investor?.countryId || "-"} />
        </div>
      </section>

      {investment && (
        <section className="card">
          <div className="card__head"><div className="card__title">Investment</div></div>
          <div className="card__body">
            <KeyVal label="Investment #" value={investment.id} />
            <KeyVal label="Amount" value={investment.amount} />
            <KeyVal label="Currency" value={investment.currency} />
            <KeyVal label="Campaign" value={investment.campaign_id} />
          </div>
        </section>
      )}

      <section className="card">
        <div className="card__head"><div className="card__title">Latest classifications</div></div>
        <div className="card__body">
          {ctx.classifications?.length ? (
            ctx.classifications.map((c) => (
              <ListItem key={c.id} meta={new Date(c.createdAt).toLocaleDateString()}>
                <strong>{c.classification}</strong>{" "}
                <span className="muted">({c.status})</span>
                {classificationCriteria(c).map((line, i) => (
                  <div key={i} className="muted" style={{ fontSize: "var(--t-xs)", marginTop: 2 }}>
                    {line}
                  </div>
                ))}
              </ListItem>
            ))
          ) : (
            <Empty>No classifications on file.</Empty>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head"><div className="card__title">Suitability failures</div></div>
        <div className="card__body">
          {ctx.suitability_failures?.length ? (
            ctx.suitability_failures.map((f) => (
              <ListItem
                key={f.id}
                meta={`${new Date(f.createdAt).toLocaleDateString()}${f.final_decision ? `, ${f.final_decision}` : ""}`}
              >
                <strong>Attempt {f.attempt_number}</strong> · {f.escalation_type}
              </ListItem>
            ))
          ) : (
            <Empty>No failures recorded.</Empty>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head"><div className="card__title">Recent KYC history</div></div>
        <div className="card__body">
          {ctx.kyc_logs?.length ? (
            ctx.kyc_logs.slice(0, 5).map((k, i) => (
              <ListItem key={k.id || i} meta={k.timestamp ? new Date(k.timestamp).toLocaleDateString() : "-"}>
                {k.status || k.verification_status || "-"}
              </ListItem>
            ))
          ) : (
            <Empty>No KYC entries.</Empty>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head"><div className="card__title">Accreditation history</div></div>
        <div className="card__body">
          {ctx.accreditation_logs?.length ? (
            ctx.accreditation_logs.slice(0, 5).map((a, i) => (
              <ListItem key={a.id || i} meta={a.timestamp ? new Date(a.timestamp).toLocaleDateString() : "-"}>
                {a.status || a.action || "-"}
              </ListItem>
            ))
          ) : (
            <Empty>No accreditation entries.</Empty>
          )}
        </div>
      </section>
    </div>
  );
}

export default ApprovalContextCards;
